// src/ui/GuestLinkModal.jsx — mint a link for someone who is not on Allo, and
// keep the record of everyone you have used one with.
//
// Two halves, deliberately in one place:
//
//   CREATE   how long it lives (1 / 3 / 7 / 15 days) and whether it needs a
//            passcode, then the link itself with a copy button.
//   HISTORY  every link ever made — live ones with their countdown, and the
//            ended ones with the name of the person who used them.
//
// 🟢 THE HISTORY IS THE POINT OF THE SECOND HALF. The conversation is deleted
//    when the link expires; the row is not. "Who have I talked to this way, and
//    when" is the one thing the owner keeps, so an expired link is shown as a
//    completed thing rather than hidden as a dead one.

import React, { useEffect, useMemo, useState } from 'react';
import {
  LINK_DAYS, createGuestLink, revokeGuestLink, watchMyGuestLinks, timeLeftLabel, repairMyAccount,
} from '../services/guestLinks';
import { X, Link as LinkIcon, Lock, Timer, Check, Trash, Person } from './icons';

const DAY_LABEL = { 1: '1 day', 3: '3 days', 7: '1 week', 15: '15 days' };

export default function GuestLinkModal({ myUid, onClose }) {
  const [tab, setTab] = useState('new');
  const [days, setDays] = useState(1);
  const [wantPasscode, setWantPasscode] = useState(false);
  const [passcode, setPasscode] = useState('');
  const [note, setNote] = useState('');
  const [busy, setBusy] = useState(false);
  const [made, setMade] = useState(null);
  const [copied, setCopied] = useState(false);
  const [err, setErr] = useState('');
  const [links, setLinks] = useState([]);
  const [fixing, setFixing] = useState(false);
  const [fixed, setFixed] = useState(null);

  /* 🟥 CHAT LINKS ONLY. Call links live in the same collection and would
     otherwise appear here as "Waiting for someone to join" — a thread that will
     never exist, with an End button that hangs up on a phone call. They have
     their own list, in the Calls column. */
  useEffect(() => watchMyGuestLinks(myUid, setLinks, 'chat'), [myUid]);

  const live = useMemo(() => links.filter((l) => l.live), [links]);
  const past = useMemo(() => links.filter((l) => !l.live), [links]);

  const create = async () => {
    setBusy(true); setErr('');
    try {
      const r = await createGuestLink({
        days,
        passcode: wantPasscode ? passcode : '',
        note: note.trim(),
      });
      setMade(r);
      setPasscode(''); setNote('');
    } catch (e) {
      setErr(e.code === 'too_many_links'
        ? 'You have a lot of open links already — end one first.'
        : 'Could not create the link. Try again.');
    }
    setBusy(false);
  };

  const copy = async (url) => {
    try {
      await navigator.clipboard.writeText(url);
      setCopied(true);
      setTimeout(() => setCopied(false), 1800);
    } catch { /* the field is selectable — nothing is lost */ }
  };

  return (
    <div className="overlay" onClick={onClose}>
      <div className="gl-modal" onClick={(e) => e.stopPropagation()}>
        <div className="gl-head">
          <b><LinkIcon width={17} height={17} /> Chat with someone not on Allo</b>
          <button className="icon-btn" onClick={onClose} aria-label="Close"><X /></button>
        </div>

        <div className="gl-tabs">
          <button className={tab === 'new' ? 'on' : ''} onClick={() => setTab('new')}>New link</button>
          <button className={tab === 'list' ? 'on' : ''} onClick={() => setTab('list')}>
            My links{links.length ? <i>{links.length}</i> : null}
          </button>
          <button className={tab === 'fix' ? 'on' : ''} onClick={() => setTab('fix')}>Repair</button>
        </div>

        {tab === 'fix' ? (
          <div className="gl-body">
            <p className="gl-lede">
              Opening your <b>own</b> link in a browser where you were already signed in used to
              run the join as you — overwriting your name, username and photo, and marking your
              account as a temporary guest. That is fixed now, but an account it already hit
              stays broken until this runs.
            </p>
            <p className="gl-lede">
              Your name and photo are still mirrored in every chat you are in and on every post
              you wrote, so this reads them back rather than asking you to remember them. It is
              safe to run when nothing is wrong.
            </p>
            <button className="lg-go" disabled={fixing} onClick={async () => {
              setFixing(true); setFixed(null);
              try {
                const r = await repairMyAccount();
                setFixed(r.report || {});
              } catch (e) {
                setFixed({ error: e?.code || e?.message || 'failed' });
              }
              setFixing(false);
            }}>
              {fixing ? 'Checking…' : 'Repair my account'}
            </button>
            {fixed && (
              <div className="gl-fixed">
                {fixed.error ? (
                  <b>Couldn’t repair — {String(fixed.error)}</b>
                ) : (
                  <>
                    <b>
                      {(fixed.clearedFlags?.length || Object.keys(fixed.restored || {}).length)
                        ? 'Repaired.' : 'Nothing to repair — this account is fine.'}
                    </b>
                    {fixed.clearedFlags?.length ? <div>Removed: {fixed.clearedFlags.join(', ')}</div> : null}
                    {Object.entries(fixed.restored || {}).map(([k, v]) => (
                      <div key={k}>Restored {k}: {String(v).slice(0, 60)}</div>
                    ))}
                    {fixed.deletedChat ? <div>Deleted the self-chat it created</div> : null}
                    {fixed.revokedLinks?.length ? <div>Revoked {fixed.revokedLinks.length} bad link(s)</div> : null}
                    <div className="gl-fixed-note">Reload the web and restart the app to see it.</div>
                  </>
                )}
              </div>
            )}
          </div>
        ) : tab === 'new' ? (
          <div className="gl-body">
            {!made ? (
              <>
                <p className="gl-lede">
                  They open the link in any browser — phone, tablet or computer — give a name
                  and an email, and you can talk. Everything is deleted when the link expires.
                </p>

                <div className="gl-label">How long should it last?</div>
                <div className="gl-days">
                  {LINK_DAYS.map((d) => (
                    <button key={d} className={days === d ? 'on' : ''} onClick={() => setDays(d)}>
                      {DAY_LABEL[d]}
                    </button>
                  ))}
                </div>

                <label className="gl-toggle">
                  <input type="checkbox" checked={wantPasscode} onChange={(e) => setWantPasscode(e.target.checked)} />
                  <span><Lock width={13} height={13} /> Require a passcode</span>
                </label>
                {wantPasscode && (
                  <input
                    className="gl-input" value={passcode} maxLength={24}
                    onChange={(e) => setPasscode(e.target.value)}
                    placeholder="Passcode you will tell them separately"
                  />
                )}

                <input
                  className="gl-input" value={note} maxLength={40}
                  onChange={(e) => setNote(e.target.value)}
                  placeholder="Label, so you know whose link this is (optional)"
                />

                {err && <div className="gl-err">{err}</div>}

                <button className="gl-go" onClick={create} disabled={busy || (wantPasscode && !passcode)}>
                  {busy ? 'Creating…' : 'Create the link'}
                </button>
                <p className="gl-fine">
                  One link, one person. Create another for someone else.
                </p>
              </>
            ) : (
              <div className="gl-made">
                <div className="gl-made-h"><Check width={16} height={16} /> Link ready</div>
                <div className="gl-url">
                  <input readOnly value={made.url} onFocus={(e) => e.target.select()} />
                  <button onClick={() => copy(made.url)}>{copied ? 'Copied' : 'Copy'}</button>
                </div>
                <div className="gl-made-meta">
                  <span><Timer width={13} height={13} /> {DAY_LABEL[made.durationDays]}</span>
                  {made.needsPasscode && <span><Lock width={13} height={13} /> Passcode required</span>}
                </div>
                <p className="gl-fine">
                  Send it however you like. Tell them the passcode a different way — a link and its
                  passcode in the same message protect nothing.
                </p>
                <button className="gl-again" onClick={() => setMade(null)}>Create another</button>
              </div>
            )}
          </div>
        ) : (
          <div className="gl-body gl-list">
            {links.length === 0 && <div className="gl-empty">No links yet.</div>}

            {live.length > 0 && <div className="gl-label">Active</div>}
            {live.map((l) => (
              <div className="gl-row" key={l.id}>
                <span className="gl-row-ava">
                  {l.guest?.image ? <img src={l.guest.image} alt="" /> : <Person width={16} height={16} />}
                </span>
                <span className="gl-row-id">
                  <b>{l.guest?.name || l.note || 'Waiting for someone to join'}</b>
                  <i>
                    {l.guest?.email ? `${l.guest.email} · ` : ''}{timeLeftLabel(l.expiresAt)}
                    {l.passcodeHash ? ' · passcode' : ''}
                  </i>
                </span>
                <button className="gl-end" title="End now" onClick={() => revokeGuestLink(l.id)}>
                  <Trash width={15} height={15} />
                </button>
              </div>
            ))}

            {past.length > 0 && <div className="gl-label">Ended</div>}
            {past.map((l) => (
              <div className="gl-row done" key={l.id}>
                <span className="gl-row-ava">
                  {l.guest?.image ? <img src={l.guest.image} alt="" /> : <Person width={16} height={16} />}
                </span>
                <span className="gl-row-id">
                  <b>{l.guest?.name || l.note || 'Never used'}</b>
                  <i>
                    {l.guest?.email ? `${l.guest.email} · ` : ''}
                    {l.status === 'revoked' ? 'ended early' : 'expired'}
                    {l.createdAt ? ` · ${new Date(l.createdAt).toLocaleDateString()}` : ''}
                  </i>
                </span>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
